// LeadsKanban.tsx — a BOARD APP: the sales pipeline as a kanban.
//
// Replaces the static Business board (Erez, 2026-07-26: "delete that board and
// create a leads board, like a Kanban board in Jarvis"). Ben's board had the
// right shape but the deals were HARDCODED in the TSX and it had drifted —
// Falcon, Air Duct and Plus500 were live engagements that never appeared on it.
// Now it reads `pipeline_deals`, so a stage change is a row edit, no deploy.
//
// Ben's two kanbans (Leads + Clients) are unified into ONE funnel. They were
// always one funnel — a client is just a later stage — and two boards is how
// Max Security ended up looking healthy on one while being stuck on the other.
//
// PARTNER is deliberately outside the funnel and greyed: Boiling Offers is a
// partnership we do not bill, and it must never flatter the pipeline.
//
// Sibling of SkillsApp / OpenSourceApp / AgentBrainApp — same 1700x1180 frame
// and one-card chrome. Columns scroll horizontally INSIDE the frame, which is
// why the scroller carries `nowheel` (a wheel inside a React Flow node pans the
// canvas otherwise).

import { useEffect, useMemo, useState } from "react";
import { Loader2, Flame, AlertTriangle } from "lucide-react";
import { useApi } from "@/lib/api";

const T = {
  ink: "#1B211E", ink2: "#4A534D", faint: "#8C948E",
  card: "#FFFFFF", paper: "#FAF7F2", bone: "#F2EDE4",
  line: "#ECE7DF", hair: "#E3DED4",
  green: "#3F8F6F", greenDeep: "#2E6B52", greenWash: "#EEF5F1",
};
const FONT = "'Inter Tight', Inter, system-ui, -apple-system, sans-serif";
const MONO = "ui-monospace, SFMono-Regular, Menlo, monospace";

const W = 1700;
const H = 1180;
const COL_W = 300;

type Deal = {
  id: string; slug: string; company: string; person: string | null;
  stage: string; temp: "hot" | "warm" | "cold" | null;
  opportunity: string | null; state: string | null;
  next_step: string | null; next_date: string | null;
  value_note: string | null; deal_value: string | null; currency: string;
  lead_source: string | null; owner_agent: string | null;
};

// The funnel, in order. `partner` sits last and reads as out-of-funnel.
const STAGES: { id: string; label: string; accent: string; note?: string }[] = [
  { id: "intro",     label: "Intro",     accent: "#7E8A86" },
  { id: "scoping",   label: "Scoping",   accent: "#B7791F" },
  { id: "proposal",  label: "Proposal",  accent: "#B0682E" },
  { id: "pilot",     label: "Pilot",     accent: "#2F6BB0" },
  { id: "discovery", label: "Discovery", accent: "#6B4FA0", note: "signed · delivery not started" },
  { id: "building",  label: "Building",  accent: "#2C7E9E" },
  { id: "live",      label: "Live",      accent: "#2E7D5B" },
  { id: "partner",   label: "Partner",   accent: "#8A948F", note: "built together · not billed" },
];

const TEMP: Record<string, string> = { hot: "#C0492F", warm: "#B7791F", cold: "#7E8A86" };

function money(v: string | null, currency: string): string | null {
  if (!v) return null;
  const n = Number(v);
  if (!Number.isFinite(n)) return null;
  const sym = currency === "ILS" ? "₪" : currency === "USD" ? "$" : "";
  return `${sym}${n.toLocaleString()}`;
}

export function LeadsKanban() {
  const api = useApi();
  const [deals, setDeals] = useState<Deal[] | "loading">("loading");

  useEffect(() => {
    let dead = false;
    (async () => {
      try {
        const r = await api("/api/pipeline");
        const rows = (await r.json()) as Deal[];
        if (!dead) setDeals(rows);
      } catch { if (!dead) setDeals([]); }
    })();
    return () => { dead = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const byStage = useMemo(() => {
    const m = new Map<string, Deal[]>();
    if (deals === "loading") return m;
    for (const d of deals) {
      if (!m.has(d.stage)) m.set(d.stage, []);
      m.get(d.stage)!.push(d);
    }
    return m;
  }, [deals]);

  // Money in the funnel EXCLUDES partner — a partnership must never flatter it.
  const totals = useMemo(() => {
    if (deals === "loading") return { priced: 0, value: 0, unpriced: 0 };
    const funnel = deals.filter(d => d.stage !== "partner" && d.stage !== "lost");
    const priced = funnel.filter(d => d.deal_value);
    return {
      priced: priced.length,
      value: priced.reduce((a, d) => a + Number(d.deal_value ?? 0), 0),
      unpriced: funnel.length - priced.length,
    };
  }, [deals]);

  if (deals === "loading") {
    return (
      <div className="nopan nodrag" style={{ width: W, height: H, display: "flex", alignItems: "center", justifyContent: "center", background: T.card, border: `1px solid ${T.hair}`, borderRadius: 20, fontFamily: FONT, color: T.faint, fontSize: 13, gap: 8, pointerEvents: "auto" }}>
        <Loader2 className="animate-spin" style={{ width: 14, height: 14 }} /> Loading pipeline…
      </div>
    );
  }

  return (
    <div
      className="nopan nodrag"
      style={{ width: W, height: H, display: "flex", flexDirection: "column", fontFamily: FONT, background: T.card, border: `1px solid ${T.hair}`, borderRadius: 20, overflow: "hidden", boxShadow: "0 8px 24px rgba(25,29,27,0.05)", pointerEvents: "auto" }}
    >
      {/* header — the one honest number */}
      <div style={{ padding: "20px 24px 16px", borderBottom: `1px solid ${T.line}`, flexShrink: 0 }}>
        <div style={{ display: "flex", alignItems: "baseline", gap: 14, flexWrap: "wrap" }}>
          <span style={{ fontSize: 19, fontWeight: 700, color: T.ink, letterSpacing: "-0.01em" }}>The pipeline</span>
          <span style={{ fontFamily: MONO, fontSize: 11.5, color: T.ink2 }}>
            {deals.length} deals · {money(String(totals.value), "ILS")} priced across {totals.priced}
          </span>
          {totals.unpriced > 0 && (
            <span style={{ display: "inline-flex", alignItems: "center", gap: 5, fontFamily: MONO, fontSize: 11.5, color: "#B0682E", background: "#FBF2EA", border: "1px solid #B0682E", borderRadius: 6, padding: "2px 8px" }}>
              <AlertTriangle style={{ width: 11, height: 11 }} />
              {totals.unpriced} carry no value — the pipeline is still unmeasurable
            </span>
          )}
        </div>
      </div>

      {/* columns */}
      <div className="nowheel nodrag nopan" style={{ flex: 1, overflow: "auto", padding: "18px 24px 24px", pointerEvents: "auto" }}>
        <div style={{ display: "flex", gap: 16, alignItems: "flex-start", minWidth: "min-content" }}>
          {STAGES.map(s => {
            const items = byStage.get(s.id) ?? [];
            const dim = s.id === "partner";
            return (
              <div key={s.id} style={{ width: COL_W, flexShrink: 0, display: "flex", flexDirection: "column", gap: 10, opacity: dim ? 0.78 : 1 }}>
                <div style={{ borderTop: `3px solid ${s.accent}`, paddingTop: 9 }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
                    <span style={{ fontFamily: MONO, fontSize: 12, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: s.accent }}>
                      {s.label}
                    </span>
                    <span style={{ fontFamily: MONO, fontSize: 11, color: T.faint }}>{items.length}</span>
                  </div>
                  {s.note && <div style={{ fontSize: 10.5, color: T.faint, marginTop: 2 }}>{s.note}</div>}
                </div>

                {items.length === 0 && (
                  <div style={{ fontSize: 11.5, color: T.faint, fontStyle: "italic", padding: "8px 2px" }}>empty</div>
                )}

                {items.map(d => (
                  <div key={d.id} style={{ background: T.paper, border: `1px solid ${T.hair}`, borderRadius: 12, padding: "12px 13px", display: "flex", flexDirection: "column", gap: 7 }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
                      <span style={{ fontSize: 13.5, fontWeight: 700, color: T.ink, lineHeight: 1.25 }}>{d.company}</span>
                      {d.temp && (
                        <span style={{ display: "inline-flex", alignItems: "center", gap: 3, fontFamily: MONO, fontSize: 9.5, fontWeight: 700, textTransform: "uppercase", color: TEMP[d.temp] }}>
                          {d.temp === "hot" && <Flame style={{ width: 10, height: 10 }} />}{d.temp}
                        </span>
                      )}
                    </div>
                    {d.person && <div style={{ fontSize: 11.5, color: T.ink2 }}>{d.person}</div>}
                    {d.opportunity && (
                      <div style={{ fontSize: 11.5, color: T.ink2, lineHeight: 1.45 }}>{d.opportunity}</div>
                    )}
                    {d.state && (
                      <div style={{ fontSize: 11, color: T.ink2, lineHeight: 1.45, background: T.card, border: `1px solid ${T.line}`, borderRadius: 8, padding: "7px 8px" }}>
                        {d.state}
                      </div>
                    )}
                    {d.next_step && (
                      <div style={{ fontSize: 11.5, color: T.ink, lineHeight: 1.4 }}>
                        <span style={{ fontFamily: MONO, fontSize: 9.5, fontWeight: 700, letterSpacing: "0.05em", textTransform: "uppercase", color: T.greenDeep }}>next </span>
                        {d.next_step}{d.next_date ? ` · ${d.next_date}` : ""}
                      </div>
                    )}
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, borderTop: `1px solid ${T.line}`, paddingTop: 7, fontFamily: MONO, fontSize: 10, color: T.faint }}>
                      <span>{money(d.deal_value, d.currency) ?? d.value_note ?? "unpriced"}</span>
                      {d.owner_agent && <span>{d.owner_agent}</span>}
                    </div>
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default LeadsKanban;
